import React, { useCallback, useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { useIsMobile } from '../../hooks/useMediaQuery';
import {
  BeforeInstallPromptEvent,
  PWA_INSTALL_COOLDOWN_MS,
  PWA_INSTALL_DISMISSED_KEY,
} from '../../pwa/types';

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
}

function isDismissCooldownActive(): boolean {
  const raw = localStorage.getItem(PWA_INSTALL_DISMISSED_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < PWA_INSTALL_COOLDOWN_MS;
}

const InstallPrompt: React.FC = () => {
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const isStandalone = useDisplayMode();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone || isDismissCooldownActive() || !isMobile) return;

    const onBeforeInstall = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
      setShowIosHint(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    if (isIosSafari() && !isStandalone) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [isMobile, isStandalone]);

  const dismiss = useCallback(() => {
    localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, String(Date.now()));
    setVisible(false);
    setDeferredPrompt(null);
    setShowIosHint(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setVisible(false);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (!visible || isStandalone) return null;

  return (
    <div
      role="region"
      aria-label={t('pwa.install.title')}
      style={{
        position: 'fixed',
        bottom: 'max(16px, env(safe-area-inset-bottom))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(480px, calc(100vw - 24px))',
        zIndex: 10050,
        backgroundColor: '#6b21a8',
        color: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <Download size={22} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <strong style={{ display: 'block', fontSize: '15px' }}>{t('pwa.install.title')}</strong>
          <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.95 }}>
            {showIosHint ? t('pwa.install.ios_hint') : t('pwa.install.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('pwa.install.dismiss')}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            padding: 4,
          }}
        >
          <X size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={dismiss}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.5)',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          {t('pwa.install.dismiss')}
        </button>
        {!showIosHint && deferredPrompt && (
          <button
            type="button"
            onClick={() => void handleInstall()}
            aria-label={t('pwa.install.action')}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#fff',
              color: '#6b21a8',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
            }}
          >
            {t('pwa.install.action')}
          </button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
